import { memo, useMemo, useRef, useLayoutEffect, useEffect, useState } from 'react';
import { useBaseContext, useItemContext } from '../Context';
import { ItemRender } from './ItemRender';
import { Virtuoso } from 'react-virtuoso';
import { ItemV5 } from '../types';
import { Checkbox, Dropdown, Spin, Switch, Space, Menu } from 'antd';
import { useTranslate } from '@tolgee/react';
import { map } from 'lodash-es';
import Empty from 'components/Empty';
import Bricks from 'bricks.js';
import { useMemoizedFn } from 'ahooks';
import Icon, { DownOutlined } from '@ant-design/icons';
import { ReactComponent as MultiSvg } from '@knowledge/assets/multi.svg';
import useModal from 'hooks/useModal';
import { QuickSelectModal, QuickSelectModalProps } from '../components/QuickSelect';
import { CollapseOperation } from '@knowledge/knowledges/singleClassNew/CollapseOperation';

const defaultItemRender = (item: ItemV5, showSameSellerSkuTag?: boolean, repack?: () => void) => {
  return <ItemRender item={item} repack={repack} showSameSellerSkuTag={showSameSellerSkuTag} />;
};
export const ListView = memo((props: { showSameSellerSkuTag?: boolean }) => {
  const { showSameSellerSkuTag = true } = props;
  const {
    items,
    getBatchStatus,
    toggleBatch,
    fetchItemLoading,
    total,
    bindItem,
    selectAll,
    reverseSelect,
    toggleOnlyChecked,
    // addToLoadedItems,
  } = useItemContext();
  const {
    itemRender = defaultItemRender,
    column = 1,
    skuFoldStatus,
    setSkuFoldStatus,
    onlyChecked,
    onlyCheckedLoading,
  } = useBaseContext();
  const { t } = useTranslate();
  const { checked, indeterminate } = useMemo(() => {
    const ids = map(items, 'internalItemId');
    return getBatchStatus(ids);
  }, [getBatchStatus, items]);
  const bricksRef = useRef<Bricks.Instance>();
  const containerRef = useRef<HTMLDivElement>(null);

  const repack = useMemoizedFn(() => {
    if (!containerRef.current) return;
    const items = containerRef.current.querySelectorAll('div');
    const container = containerRef.current;
    const newBrick = () => {
      items.forEach((item) => {
        item.removeAttribute('data-packed');
        item.removeAttribute('style');
      });
      const instance = Bricks({
        packed: 'data-packed',
        container: container,
        sizes: [{ columns: 2, gutter: 16 }],
      });
      bricksRef.current = instance;
      instance.pack();
    };
    if (container) {
      if (container.hasAttribute('style')) {
        container.removeAttribute('style');
        items.forEach((item) => {
          item.removeAttribute('data-packed');
          item.removeAttribute('style');
        });
        requestAnimationFrame(() => {
          bricksRef.current?.pack();
          bricksRef.current?.resize(true);
        });
      } else {
        newBrick();
      }
    }
  });
  useLayoutEffect(() => {
    if (column === 1) return;
    if (fetchItemLoading) return;
    repack();
  }, [column, fetchItemLoading, repack]);

  useEffect(() => {
    if (column === 1) return;
    if (fetchItemLoading) return;
    repack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, skuFoldStatus, column, fetchItemLoading]);
  const { visible, show, hide, meta } = useModal<QuickSelectModalProps>();
  const [allLoading, setAllLoading] = useState(false);

  if (fetchItemLoading) {
    return (
      <div className="h-full flex p-[16px] overflow-hidden">
        <div className="inline-block m-auto">
          <Spin />
        </div>
      </div>
    );
  }

  if (total === 0 && !onlyChecked) {
    return (
      <div className="h-full flex p-[16px] overflow-hidden">
        <div className="inline-block m-auto">
          <Empty />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-[16px] overflow-hidden pb-0">
      <div className="h-[22px] flex-shrink-0 items-center mb-[12px] flex justify-between">
        <div className="flex items-center gap-[12px] text-[#515359]">
          <Checkbox
            onChange={(e) => {
              toggleBatch(map(items, 'internalItemId'), e.target.checked);
            }}
            checked={checked}
            indeterminate={indeterminate}
          >
            {t('voucher.本页全选')}
          </Checkbox>
          <Spin spinning={allLoading} size="small">
            <Dropdown
              overlay={
                <Menu
                  onClick={(e) => {
                    if (e.key === 'selectAll') {
                      setAllLoading(true);
                      selectAll().finally(() => {
                        setAllLoading(false);
                      });
                    } else {
                      setAllLoading(true);
                      reverseSelect().finally(() => {
                        setAllLoading(false);
                      });
                    }
                  }}
                >
                  <Menu.Item key="selectAll">{t('全部选中')}</Menu.Item>
                  <Menu.Item key="reverseSelectAll">{t('取消全部选中')}</Menu.Item>
                </Menu>
              }
            >
              <Space>
                {t('trade.更多操作')}
                <DownOutlined style={{ fontSize: '12px' }} />
              </Space>
            </Dropdown>
          </Spin>
        </div>
        <div className="flex items-center text-[#515359] ">
          <div className="flex items-center mr-[12px]">
            {t('aide.仅看已选')}
            <Switch
              className="!ml-[6px]"
              loading={onlyCheckedLoading}
              checked={onlyChecked}
              onChange={() => {
                toggleOnlyChecked(!onlyChecked);
              }}
            />
          </div>
          {!bindItem && (
            <div
              className="cursor-pointer mr-[12px] hover:text-[#3366ff]"
              onClick={() => {
                show({
                  // callback: (items) => {
                  //   addToLoadedItems(items);
                  //   setTimeout(() => {
                  //     toggleBatch(map(items, 'internalItemId'), true);
                  //   });
                  // },
                });
              }}
            >
              <Icon component={MultiSvg} className="!hover:text-[#3366ff]" />
              <span className="ml-[4px]">{t('knowledge.item.select.mode.mult')}</span>
            </div>
          )}
          {visible && meta && (
            <QuickSelectModal {...meta} visible={visible} onCancel={() => hide()} />
          )}
          <CollapseOperation
            className="ml-[12px]"
            status={skuFoldStatus}
            setStatus={setSkuFoldStatus}
          />
        </div>
      </div>
      {column === 1 ? (
        total === 0 ? (
          <div className="inline-block m-auto">
            <Empty />
          </div>
        ) : (
          <Virtuoso
            data={items}
            totalCount={items.length}
            itemContent={(index, item) => (
              <div
                className=" pt-[10px] pr-[13px] pb-[12px] pl-[8px] rounded-[2px] border border-solid border-[#d3d7e0] mb-[12px]"
                key={`${item.internalItemId}-${index}`}
              >
                {itemRender(item, showSameSellerSkuTag)}
              </div>
            )}
          />
        )
      ) : total === 0 ? (
        <div className="inline-block m-auto flex items-center">
          <Empty />
        </div>
      ) : (
        <div className="overflow-y-auto overflow-x-hidden">
          <div ref={containerRef}>
            {items.map((item) => {
              return (
                <div className="w-[calc(50%-8px)] overflow-hidden" key={item.internalItemId}>
                  <div className="rounded-[2px] border border-solid border-[#d3d7e0] pt-[10px] pr-[13px] pb-[12px] pl-[8px]">
                    {itemRender(item, showSameSellerSkuTag, repack)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

ListView.displayName = 'ItemSelectV5ListView';
